const BASE_URLS = {
  'local': 'https://localhost:44392',
  'docker': 'http://localhost:5080',
  'dev': 'https://localhost:44392',
  'qa': 'https://apiv2-qa.bnovus.cl',
  'test': 'http://localhost:9999',
  'production': 'https://webapi-asistencia.bnovus.cl',
};

const REX_SSO_URLS = {
  'local': 'https://accounts.qa.rexmas.com',
  'docker': 'https://accounts.qa.rexmas.com',
  'dev': 'https://accounts.qa.rexmas.com',
  'qa': 'https://accounts.qa.rexmas.com',
  'test': 'https://accounts.qa.rexmas.com',
  'production': 'https://accounts.rexmas.com',
} as const;

type RexSSOEnvironment = keyof typeof REX_SSO_URLS;

const resolveRexSSOBaseUrl = (): string => {
  const envName = ((import.meta.env.VITE_ENVIRONMENT || '') as string).toLowerCase() as RexSSOEnvironment;
  return REX_SSO_URLS[envName] ?? REX_SSO_URLS.qa;
};

export const getRexSSOLogoutUrl = (): string => `${resolveRexSSOBaseUrl()}/logout`;

export const getRexSSOLoginUrl = (): string => `${resolveRexSSOBaseUrl()}/login`;

const FISCALIZADOR_DT_URLS = {
  'local': 'https://newfrontbnovusqa.bnovus.cl/DT/login.aspx',
  'docker': 'https://newfrontbnovusqa.bnovus.cl/DT/login.aspx',
  'dev': 'https://newfrontbnovusqa.bnovus.cl/DT/login.aspx',
  'qa': 'https://newfrontbnovusqa.bnovus.cl/DT/login.aspx',
  'test': 'https://newfrontbnovusqa.bnovus.cl/DT/login.aspx',
  // TODO: confirmar dominio de producción cuando el portal Fiscalizador DT esté montado en prod
  'production': 'https://newfrontbnovusqa.bnovus.cl/DT/login.aspx',
} as const;

type FiscalizadorDTEnvironment = keyof typeof FISCALIZADOR_DT_URLS;

export const getFiscalizadorDTUrl = (): string => {
  const envName = ((import.meta.env.VITE_ENVIRONMENT || '') as string).toLowerCase() as FiscalizadorDTEnvironment;
  return FISCALIZADOR_DT_URLS[envName] ?? FISCALIZADOR_DT_URLS.qa;
};

const DEVICE_MONITOR_URLS = {
  'local':      'https://monitorasistencia.bnovus.cl/monitor',
  'docker':     'https://monitorasistencia.bnovus.cl/monitor',
  'dev':        'https://monitorasistencia.bnovus.cl/monitor',
  'qa':         'https://monitorasistencia.bnovus.cl/monitor',
  'test':       'https://monitorasistencia.bnovus.cl/monitor',
  'production': 'https://monitorasistencia.bnovus.cl/monitor',
} as const;

type DeviceMonitorEnvironment = keyof typeof DEVICE_MONITOR_URLS;

const getDeviceMonitorBaseUrl = (): string => {
  const envName = ((import.meta.env.VITE_ENVIRONMENT || '') as string).toLowerCase() as DeviceMonitorEnvironment;
  return DEVICE_MONITOR_URLS[envName] ?? DEVICE_MONITOR_URLS.qa;
};

export const getDeviceMonitorUrl = (alternativeCompanyKey: string): string =>
  `${getDeviceMonitorBaseUrl()}/${alternativeCompanyKey}`;

const resolveBaseUrl = (): string => {
  const override = import.meta.env.VITE_API_BASE_URL;
  if (override) return override;

  const envName = (import.meta.env.VITE_ENVIRONMENT || '').toLowerCase();

  type Environment = 'local' | 'docker' | 'dev' | 'qa' | 'test' | 'production';
  const isEnvironment = (env: string): env is Environment => (
    env === 'local' || env === 'docker' || env === 'dev' || env === 'qa' || env === 'test' || env === 'production'
  );

  if (isEnvironment(envName)) {
    const value = BASE_URLS[envName];
    if (typeof value === 'string') return value;
  }
  // Final fallback (explicit, not mode-based): local default
  return 'https://localhost:44392';
};

export const API_CONFIG = {
  BASE_URL: resolveBaseUrl(),
  VERSION: '1',
  ENDPOINTS: {
    // Authentication (formerly LOGIN)
    AUTHENTICATION: {
      LOGIN: '/authentication/login',
      RECOVER_PASSWORD: '/authentication/password/recover',
      UPDATE_PASSWORD: '/authentication/password/update',
      SSO_CHECK_PENDING: '/authentication/sso/check-pending',
      SSO_ACCOUNT_INFO: '/authentication/sso/account-info',
      SSO_LINK_ACCOUNT: '/authentication/sso/link-account',
    },
    MENU: '/menu',
    AGENTE: '/Agente',
    EXPRESS: '/Express',
    // Attendance monitoring counters (Informes › Gestión › Monitoreo Asistencia)
    MONITORING_COUNTERS: '/attendance/monitoring/counters',
    // Attendance dashboard "Mi equipo" filters panel (ASIS-5402 / REXASIS-001)
    ATTENDANCE_MY_TEAM_FILTERS: '/attendance/my-team/filters',
    ATTENDANCE_MY_TEAM_FILTERS_EMPLOYEES: '/attendance/my-team/filters/employees',
    // Employee Attendance - consolidated under /employees/{employeeId}/attendance
    ATTENDANCE: {
      GET_EMPLOYEE_ATTENDANCE: (employeeId: number) => `/employees/${employeeId}/attendance`,
      REGISTER: (employeeId: number) => `/employees/${employeeId}/attendance`,
      REGISTER_MANUAL: (employeeId: number) => `/employees/${employeeId}/attendance/management/manual`,
      GET_ATTENDANCE_DETAIL: (employeeId: number, attendanceId: number) => `/employees/${employeeId}/attendance/${attendanceId}`,
      GET_MANAGED_MARKS: (employeeId: number) => `/employees/${employeeId}/attendance/managedMarks`,
      UPDATE_MANAGED_MARK: (employeeId: number, markId: string) => `/employees/${employeeId}/attendance/managedMarks/${markId}`,
      DASHBOARD: () => '/attendance/my-attendance/daily-status',
      TODAY_MARKS: () => '/attendance/my-attendance/today-marks',
      CURRENT_WEEK: () => '/attendance/my-attendance/current-week-summary',
      DASHBOARD_MONTHLY_RESUME: () => '/attendance/my-attendance/dashboard/monthly-resume',
      GET_MONTHLY_CUTOFFS: '/attendance/my-attendance/available-monthly-cutoffs',
      GET_ALL_MONTHLY_CUTOFFS: () => '/attendance/my-attendance/all-monthly-cutoffs',
      DASHBOARD_EXPORT: () => '/attendance/my-attendance/dashboard/export',
      EXPORT_MONTHLY_REPORT: '/attendance/monthly-reports',
      CONFIGURATION: '/attendance/configuration',
      PROGRESSIVITIES: '/attendance/configuration/progressivities',
      MARKS: '/attendance/marks',
      REPORTS_MARKS: '/attendance/reports/marks',
      REPORTS_MARKS_EXPORT: '/attendance/reports/marks/export',
      MARKS_SEARCH: '/attendance/marks/search',
      MARKS_EXPORT: '/attendance/marks/export',
      TOGGLE_MARK_ENABLED: (markId: number) => `/attendance/management/marks/${markId}/toggle-enabled`,
    },
    // Monthly reports - new flat URLs
    MONTHLY_CUTS: '/monthlycuts',
    MONTHLY_CUTS_MANAGEMENT: '/monthlycuts/management',
    MONTHLY_CUTS_MANAGEMENT_COUNTERS: '/monthlycuts/management/counters',
    MONTHLY_CUTS_MANAGEMENT_EXPORT: '/monthlycuts/management/file',
    MONTHLY_CUTS_MANAGEMENT_GROUPS_AVAILABLE: '/monthlycuts/management/groups/available',
    MONTHLY_CUTS_MANAGEMENT_GROUPS_ASSIGNED: '/monthlycuts/management/groups/assigned',
    MONTHLY_CUTS_CONCEPT_GENERATION: '/monthlycuts/concept-generation',
    MONTHLY_CUTS_CONCEPT_GENERATION_PROGRESS: (monthlyCutId: number) =>
      `/monthlycuts/${monthlyCutId}/concept-generation/progress`,
    MONTHLY_CUTS_PAYROLL_SUBMISSIONS: '/monthlycuts/payroll-submissions',
    MONTHLY_CUTS_PAYROLL_AVAILABILITY: '/monthlycuts/payroll-submissions/availability',
    MONTHLY_CUTS_PAYROLL_SUBMISSION_RESULT: (jobId: string) =>
      `/monthlycuts/payroll-submissions/${jobId}`,
    MONTHLY_REPORT: '/monthlyreport',
    MONTHLY_REPORT_HHEE_EXPORT: '/monthlyreport/hhee/export',
    SHIFT: {
      SUBMIT_REQUEST: (employeeId: number) => `/employees/${employeeId}/shift/change-requests`,
      GET_REQUESTS: (employeeId: number) => `/employees/${employeeId}/shift/change-requests`,
      GET_COMPANY_REQUESTS: () => '/shift/change-requests',
      GET_COMPANY_REQUEST_COUNTERS: () => '/shift/change-requests/counters',
      EXPORT_COMPANY_REQUESTS: '/shift/change-requests/file',
      RESOLVE_REQUEST: (requestId: number) => `/shift/change-requests/${requestId}/resolution`,
      BULK_APPROVE_REQUESTS: '/shift/change-requests/bulk-approval',
      BULK_REJECT_REQUESTS: '/shift/change-requests/bulk-rejection',
      GET_COMPANY_SHIFTS: '/attendance/shifts',
      CREATE_COMPANY_SHIFT: '/attendance/shifts',
      GET_SCHEDULES: '/attendance/shifts/schedules',
    },
    LOCATION: {
      GET_EMPLOYEE_ENABLED_LOCATIONS: (employeeId: number) => `/employees/${employeeId}/locations`,
      GENERATE_MAP: '/map/generate',
    },
    SERVER_TIME: {
      GET: '/ServerTime',
    },
    EMPLOYEES: {
      LIST: '/employees',
      LOOKUP: '/employees/lookup',
      FILTERED: '/employees/filtered',
      BASIC: '/employees/basic',
      ENABLE_DISABLE: (employeeId: number) => `/employees/${employeeId}/enabled`,
      CREATE: '/employees',
      PROFILE: (employeeId: number) => `/employees/${employeeId}`,
      UPDATE: (employeeId: number) => `/employees/${employeeId}`,
      EXPORT: '/employees/contracts/file',
      APP_MOBILE_PROFILES: '/employees/app-mobile-profiles',
      APP_MOBILE_PROFILES_FILE: '/employees/app-mobile-profiles/file',
    },
    SECURITY_GROUPS: {
      BY_PERMISSION: '/securityGroups/byPermission',
    },
    HOLDING: {
      COMPANIES: '/catalog/company/holding-companies',
    },
    COMPANY: {
      LIST: '/companies',
      CREATE: '/companies',
      UPDATE: (companyId: number) => `/companies/${companyId}`,
      GET_BY_ID: (companyId: number) => `/companies/${companyId}`,
      UPDATE_LOGO: (companyId: number) => `/companies/${companyId}/logo`,
    },
    GEOGRAPHY: {
      REGIONS: (country: string) => `/geography/${country}/regions`,
      COMMUNES: (country: string, regionId: number) => `/geography/${country}/regions/${regionId}/communes`,
    },
    CATALOG: {
      MARITAL_STATUSES: (countryId: number, forceReload?: boolean) => 
        `/catalog/marital-statuses?countryId=${countryId}${forceReload ? '&forceReload=true' : ''}`,
      INCIDENT_ERROR_TYPES: '/catalog/incidentErrorTypes',
      MARKING_EVENTS: '/catalog/markingEvents',
      AREAS: '/catalog/areas',
      POSITIONS: (areaIds?: string) =>
        areaIds ? `/catalog/positions?areaIds=${areaIds}` : '/catalog/positions',
      COST_CENTERS: '/catalog/cost-centers',
      UNIONS: '/catalog/unions',
      CONTRACT_TYPES: '/catalog/contract/types',
      CONTRACT_MODALITIES: '/catalog/contract/modalities',
      CONTRACT_STATUS: '/catalog/contract/status',
      LOCATIONS: '/catalog/locations',
      COMPANY_CONTROLS: '/catalog/company-controls',
      INCIDENT_NOTIFICATION_RECIPIENTS: '/catalog/incident-notification-recipients',
      ROLES: '/catalog/roles',
      CUSTOM_ROLES: '/catalog/custom-roles',
      APP_MOBILE_PROFILE_TYPES: '/catalog/app-mobile-profile-types',
      PERMISSION_LEVELS: '/catalog/permission-levels',
      COMPANY_FOLDERS: '/catalog/company-folders',
      DOCUMENT_TYPES: '/catalog/document-types',
      TIMEZONES: '/catalog/timezones',
      SHIFT_GROUPS: '/catalog/shiftgroups',
      MONITORING_SECURITY_GROUPS: '/catalog/monitoring/security-groups',
      WORKING_DAY_TYPES: '/catalog/working-day-types',
      HOLIDAY_TYPES: '/catalog/holiday-types',
      ATTENDANCE_CONCEPT_HANDLINGS: '/catalog/attendance-concepts/handlings',
      ATTENDANCE_CONCEPT_FORMULAS: '/catalog/attendance-concepts/formulas',
      ASYNC_JOB_PROCESSES: '/catalog/async-job-processes',
      MONTHLY_CUTS_YEARS: '/catalog/monthly-cuts-years',
    },
    ORGANIZATION: {
      AREAS: '/areas',
      AREA_BY_ID: (areaId: number) => `/areas/${areaId}`,
      ENABLE_AREA: (areaId: number) => `/areas/${areaId}/enable`,
      DISABLE_AREA: (areaId: number) => `/areas/${areaId}/disable`,
      EXPORT_AREAS: '/areas/export',
      POSITIONS: '/positions',
      POSITION_BY_ID: (positionId: number) => `/positions/${positionId}`,
      ENABLE_POSITION: (positionId: number) => `/positions/${positionId}/enable`,
      DISABLE_POSITION: (positionId: number) => `/positions/${positionId}/disable`,
      EXPORT_POSITIONS: '/positions/export',
      COST_CENTERS: '/cost-centers',
      COST_CENTER_BY_ID: (costCenterId: number) => `/cost-centers/${costCenterId}`,
      ENABLE_COST_CENTER: (costCenterId: number) => `/cost-centers/${costCenterId}/enable`,
      DISABLE_COST_CENTER: (costCenterId: number) => `/cost-centers/${costCenterId}/disable`,
      EXPORT_COST_CENTERS: '/cost-centers/export',
    },
    PAYMENT: {
      CONCEPTOS_PAGO: '/attendance/conceptos-pago',
    },
    ABSENCE: {
      LIST: '/attendance/absence',
      FILE: '/attendance/absence/file',
      SCOPE_LIST: '/attendance/absence/scope',
      EMPLOYEES: '/attendance/absence/employees',
      WORKDAY_TYPES: '/attendance/absence/workday-types',
      RETURN_DATE: '/attendance/absence/return-date',
    },
    HOLIDAYS: {
      LIST: '/attendance/holidays',
      BY_ID: (id: number) => `/attendance/holidays/${id}`,
      BULK: '/attendance/holidays/bulk',
      EXPORT: '/attendance/holidays/file',
      REGION_LIST: '/attendance/holidays-by-region',
      REGION_BY_ID: (id: number) => `/attendance/holidays-by-region/${id}`,
      REGION_EXPORT: '/attendance/holidays-by-region/file',
    },
    ATTENDANCE_CONCEPTS: {
      LIST: '/attendance/concepts',
      BY_ID: (id: number) => `/attendance/concepts/${id}`,
      SYNC: '/attendance/concepts/synchronizations',
      FILE: '/attendance/concepts/file',
    },
    EMPLOYEE_INCIDENTS: {
      REGISTER: (employeeId: number) => `/employees/${employeeId}/attendances/incidents`,
      LIST: (employeeId: number) => `/employees/${employeeId}/attendances/incidents`,
      DETAIL: (employeeId: number, incidentId: number) => `/employees/${employeeId}/attendances/incidents/${incidentId}`,
      FILE: (employeeId: number) => `/employees/${employeeId}/attendances/incidents/file`,
      LIST_MANAGEMENT: () => '/attendances/incidents',
      FILE_MANAGEMENT: () => '/attendances/incidents/file',
    },
    EMPLOYEE_CONTRACT: {
      LIST: (employeeId: number) => `/employees/${employeeId}/contracts`,
      GET: (employeeId: number, contractId: number) => `/employees/${employeeId}/contracts/${contractId}`,
      CREATE: (employeeId: number) => `/employees/${employeeId}/contracts`,
      UPDATE: (employeeId: number, employeeContractId: number) => `/employees/${employeeId}/contracts/${employeeContractId}`,
      DELETE: (employeeId: number, employeeContractId: number) => `/employees/${employeeId}/contracts/${employeeContractId}`,
    },
    QUERIES: {
      RELOAD: '/queries/reload',
    },
    SCHEDULES: {
      LIST: '/schedules',
      FILE: '/schedules/file',
    },
    WORKING_DAYS: {
      LIST: '/attendance/working-days',
      FILE: '/attendance/working-days/file',
      CREATE: '/attendance/working-days',
      UPDATE: (id: number) => `/attendance/working-days/${id}`,
      TOGGLE_STATUS: (id: number) => `/attendance/working-days/${id}/status`,
    },
    AUTHORIZATION: {
      ROLES: {
        LIST: '/authorization/roles',
        CREATE: '/authorization/roles',
        UPDATE: (roleId: string) => `/authorization/roles/${roleId}`,
        GET: (roleId: string) => `/authorization/roles/${roleId}`,
        STATUS: (roleId: string) => `/authorization/roles/${roleId}/status`,
      },
      EMPLOYEE_PERMISSION: (targetEmployeeId: number, code: string) =>
        `/authorization/employee-permission/${targetEmployeeId}/resolved/${code}`,
      EMPLOYEE_PERMISSION_BATCH: (code: string) =>
        `/authorization/employee-permission/resolved/batch/${code}`,
      SYSTEM_PAGES: '/authorization/system-pages',
      ROLE_SECTIONS: (roleId: number, interfaceId: number) => `/authorization/roles/${roleId}/interfaces/${interfaceId}/sections`,
      ROLE_PERMISSIONS: (roleId: number) => `/authorization/roles/${roleId}/permissions`,
      ROLE_PERMISSIONS_FILE: (roleId: number) => `/authorization/roles/${roleId}/permissions/file`,
      ROLE_MEMBERS: (roleId: number) => `/authorization/roles/${roleId}/members`,
      ROLE_NON_MEMBERS: (roleId: number) => `/authorization/roles/${roleId}/non-members`,
    },
    EMPLOYEE_DOCUMENT: {
      LIST: '/employees/documents',
      DOWNLOAD: (documentId: number) => `/employees/documents/${documentId}/file`,
    },
    FRACTIONAL_LEAVE: {
      LIST: '/attendance/fractional-leave',
      CREATE: '/attendance/fractional-leave',
      UPDATE: (id: number) => `/attendance/fractional-leave/${id}`,
      TOGGLE_STATUS: (id: number) => `/attendance/fractional-leave/${id}/estado`,
      EXPORT: '/attendance/fractional-leave/file',
    },
    MEDICAL_LEAVE: {
      TYPES: '/attendance/medical-leave/types',
      CREATE: '/attendance/medical-leave',
    },
    SPECIAL_LEAVE: {
      TYPES: '/attendance/special-leave/types',
      CREATE: '/attendance/special-leave',
    },
    SECURITY_GROUPING: {
      LIST: '/securityGroups',
      CREATE: '/securityGroups',
      UPDATE: (id: number) => `/securityGroups/${id}`,
      STATUS: (id: number) => `/securityGroups/${id}/status`,
      ASSIGNED_EMPLOYEES: '/securityGroups/assignedEmployees',
      ASSIGNED_EMPLOYEES_FILE: '/securityGroups/assignedemployees/file',
      ADMINS: (id: number) => `/securityGroups/${id}/admins`,
      ADMINS_AVAILABLE: (id: number) => `/securityGroups/${id}/admins/available`,
      ADMINS_ASSIGNED: (id: number) => `/securityGroups/${id}/admins/assigned`,
      EMPLOYEES: (id: number) => `/securityGroups/${id}/employees`,
      EMPLOYEES_AVAILABLE: (id: number) => `/securityGroups/${id}/employees/available`,
      EMPLOYEES_ASSIGNED: (id: number) => `/securityGroups/${id}/employees/assigned`,
    },
    AUDIT: {
      FILTERS_ENTITIES: '/audit/filters/entities',
      FILTERS_PROGRAMS: '/audit/filters/programs',
      FILTERS_ACTIONS: '/audit/filters/actions',
      FILTERS_USERS: '/audit/filters/users',
      RECORDS: '/audit/records',
      RECORDS_EXPORT: '/audit/records/file',
    },
    ASYNC_JOBS: {
      LIST: '/async-jobs',
      TYPES: '/async-jobs/types',
      STATUS: (jobId: string) => `/async-jobs/${jobId}/status`,
      DOWNLOAD: (jobId: string) => `/async-jobs/${jobId}/download`,
    },
    CHECK_IN_LOCATION: {
      LIST: '/attendance/locations',
      CREATE: '/attendance/locations',
      UPDATE: (id: number) => `/attendance/locations/${id}`,
      ASSIGNED_EMPLOYEES: '/attendance/locations/assigned-employees',
      ASSIGNED_EMPLOYEES_EXPORT: '/attendance/locations/employees/file',
      EMPLOYEES_AVAILABLE: (locationId: number) => `/attendance/locations/${locationId}/employees/available`,
      EMPLOYEES_ASSIGNED: (locationId: number) => `/attendance/locations/${locationId}/employees/assigned`,
      EMPLOYEES_SAVE: (locationId: number) => `/attendance/locations/${locationId}/employees`,
      DELETE: (id: number) => `/attendance/locations/${id}`,
      BULK_DELETE: '/attendance/locations',
      EXPORT: '/attendance/locations/file',
      GEOLOCATION_LIST: (locationId: number) => `/attendance/locations/${locationId}/geo-locations`,
      GEOLOCATION_DELETE: (locationId: number, geolocationId: number) => `/attendance/locations/${locationId}/geo-locations/${geolocationId}`,
      GENERATE_GEOLOCATION: (locationId: number) => `/attendance/locations/${locationId}/generate-geolocation`,
      GENERATE_GEOLOCATION_POINTS: (locationId: number) => `/attendance/locations/${locationId}/geo-location-sets`,
    },
    DEVICES: {
      SUMMARY: '/devices/summary',
      LIST: '/devices',
      EXPORT: '/devices/export',
    },
    RECALCULATION: {
      OPEN_YEARS: '/recalculation/openMonthlyCuts/years',
      OPEN_MONTHS: '/recalculation/openMonthlyCuts/months',
      OPEN_DESCRIPTIONS: '/recalculation/openMonthlyCuts/descriptions',
      PROCESS_STATUS: '/recalculation/process/status',
      PROCESS_START: '/recalculation/process/start',
      PROCESS_STOP: '/recalculation/process/stop',
      PROCESS_HISTORY: '/recalculation/process/history',
      PROCESS_HISTORY_ERRORS_FILE: '/recalculation/process/errors/file',
      PROCESS_LAST_ERRORS_FILE: '/recalculation/process/last/errors/file',
    },
  },
  TIMEOUT: (() => {
    const raw = import.meta.env.VITE_API_TIMEOUT;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) return 10000;
    return parsed;
  })(),
} as const;

export const getVersionedEndpoint = (endpoint: string): string =>
  `/v${API_CONFIG.VERSION}${endpoint}`;

export interface ApiClientConfig {
  baseUrl: string;
  version: string;
  timeout: number;
}

export const apiClientsRegistry: Record<string, ApiClientConfig> = {
  core: {
    baseUrl: API_CONFIG.BASE_URL,
    version: API_CONFIG.VERSION,
    timeout: API_CONFIG.TIMEOUT,
  },
};

export const registerApiClient = (name: string, config: Partial<ApiClientConfig>) => {
  apiClientsRegistry[name] = {
    baseUrl: config.baseUrl || API_CONFIG.BASE_URL,
    version: config.version || API_CONFIG.VERSION,
    timeout: config.timeout || API_CONFIG.TIMEOUT,
  };
};
